import { useMemo, useState } from 'react';

import SelectBottomSheet from '@/components/common/BottomSheet/SelectBottomSheet';
import NonF2fDiagnosisAppInstallBottomSheet from '@/components/templates/f2f/hospital/HospitalDetailPartnerTemplate/HospitalBottomSheet/NonF2fDiagnosisAppInstallBottomSheet/NonF2fDiagnosisAppInstallBottomSheet';
import NonF2fDiagnosisAppInstallQrCodeBottomSheet from '@/components/templates/f2f/hospital/HospitalDetailPartnerTemplate/HospitalBottomSheet/NonF2fDiagnosisAppInstallQrCodeBottomSheet/NonF2fDiagnosisAppInstallQrCodeBottomSheet';
import useSearchHospitalsGenerally from '@/hooks/hospital/list/useSearchHospitalsGenerally';
import useMedicalCategories from '@/hooks/hospital/main/useMedicalCategories';
import useSearchKoreaAddressV2 from '@/hooks/hospital/search/useSearchKoreaAddressV2';
import useGetKeywordSearchMagazineInfinite from '@/hooks/search/magazine/useGetKeywordSearchMagazineInfinite';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import {
  NeighborhoodHospitalFilter,
  NeighborhoodHospitalType,
  NeighborhoodHospitalTypes,
  NonFaceToFaceDepartment,
} from '@/models/hospital';
import { KoreaAddress, Region } from '@/models/nonBenefit';
import { Client } from '@/types/app';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategorySortType,
  MedicalCategoryType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import {
  ContractType,
  MedicalCategoryVo,
  NeighborhoodHospitalQueries,
  SearchGenerallyHospitalDoctor,
  SearchGenerallyHospitalVo,
  SearchKoreaAddressV2Vo,
  SelectedRegion,
} from '@/types/hospital';
import { MagazineType } from '@/types/magazine';
import { MODAL_TYPE } from '@/types/modal';
import { KeywordSearchMagazineVo } from '@/types/search';
import { addQueryString } from '@/utils/url';

const APP_INSTALL_LINK_FROM_HOSPITAL_SEARCH = 'https://abr.ge/57p7fm';

interface NeighborhoodHospitalViewModel {
  isLoadingHospitals: boolean;
  hospitals: SearchGenerallyHospitalVo[];
  nonF2fDoctors: SearchGenerallyHospitalDoctor[];
  queries: NeighborhoodHospitalQueries;
  queryRegion: Region;
  koreaAddress: KoreaAddress;
  faqs: KeywordSearchMagazineVo[];
  magazines: KeywordSearchMagazineVo[];
  matchedCategory: { category: MedicalCategoryType; id: string } | null;
  getSearchAddressFilterModalProps: () => {
    siDoList: SearchKoreaAddressV2Vo[];
    siGunGuList: SearchKoreaAddressV2Vo[];
    dongList: SearchKoreaAddressV2Vo[];
    selectedRegion: SelectedRegion;
    onSelectAddress: (item: SearchKoreaAddressV2Vo) => void;
    onUpdateAddress: (item: SearchKoreaAddressV2Vo) => void;
    isOpened: boolean;
    isSejong: boolean;
    client: Client;
    switchRegionFilter: () => void;
  };
  isAddressFilterModalOpen: boolean;
  neighborhoodHospitalTypes: NeighborhoodHospitalTypes;
  openNeighborhoodHospitalTypeBottomSheet: () => void;
  openSearchAddressFilterModal: () => void;
  toggleNonF2fHospitalFilter: () => void;
  openAppInstallBottomSheet: () => void;
  onClickRelatedFaq: (magazinePid: string) => void;
  onClickRelatedMagazine: (magazinePid: string) => void;
  getNeighborhoodSeoProps: () => {
    title: string;
    description: string;
    canonical: string;
  };
  departments: MedicalCategoryVo[];
  openNeighborhoodKeywordBottomSheet: () => void;
}

const useNeighborhoodHospitalViewModel = (client: Client): NeighborhoodHospitalViewModel => {
  const { router, moveToPage } = useCustomRouter();
  const { showModal, closeModal } = useModal();
  const queries = router.query as NeighborhoodHospitalQueries;
  const {
    regionCode = '',
    keyword = '',
    type = NeighborhoodHospitalType.ALL,
    filter = NeighborhoodHospitalFilter.F2fHospital,
  } = queries;

  const [isAddressFilterModalOpen, setIsAddressFilterModalOpen] = useState(false);

  const neighborhoodHospitalTypes = useMemo(() => {
    return new NeighborhoodHospitalTypes();
  }, []);

  const { data: departments = [] } = useMedicalCategories({
    sortType: MedicalCategorySortType.Alphabet,
  });

  /* 지역 검색 */
  const queryRegion = new Region(regionCode);
  const { data: siDoList = [] } = useSearchKoreaAddressV2({
    siDoCode: null,
    siGunGuCode: null,
  });
  const { data: siGunGuList = [] } = useSearchKoreaAddressV2({
    siDoCode: queryRegion.excludeSejongSiDoRegionCode(),
    siGunGuCode: null,
  });
  const { data: dongList = [] } = useSearchKoreaAddressV2({
    siDoCode: queryRegion.regionCodes.siDoCode,
    siGunGuCode: queryRegion.excludeSejongSiGunGuRegionCode(),
  });

  /* 관련 컨텐츠 및 QNA */
  const { magazines: faqs } = useGetKeywordSearchMagazineInfinite({
    keyword: keyword,
    size: 10,
    contentType: MagazineType.FAQ,
  });

  const { magazines } = useGetKeywordSearchMagazineInfinite({
    keyword: keyword,
    size: 10,
    contentType: MagazineType.ARTICLE,
  });

  const { hospitals = [], isLoadingHospitals } = useSearchHospitalsGenerally(
    useSearchHospitalsGenerally.parseParameterByNeighborhoodHospital({
      keyword,
      regionCode,
      filter,
      hospitalType: type,
    }),
  );

  const nonF2fDoctors = hospitals
    .flatMap((hospital) => {
      return hospital.doctors.filter((doctor) => doctor.contractType !== ContractType.FaceToFace);
    })
    .slice(0, 5);

  const { category: matchedCategory } = useInfiniteSearchNonF2fDoctors(
    {
      request: {
        keyword: keyword,
        sortType: DoctorSortType.Random,
        page: 1,
        limit: 1,
        doctorType: DoctorType.Doctor,
        telemedicineEligibleTargetType: TelemedicineEligibleTargetType.AlwaysEligible,
      },
    },
    { enabled: !!keyword },
  );

  /* 선택된 지역 */
  const koreaAddress = new KoreaAddress(siDoList, siGunGuList, dongList);

  const openAppInstallBottomSheet = () => {
    if (client.isMobile) {
      return showModal({
        type: MODAL_TYPE.BASIC,
        props: {
          body: (
            <NonF2fDiagnosisAppInstallBottomSheet
              appInstallLink={APP_INSTALL_LINK_FROM_HOSPITAL_SEARCH}
              onClickOverlay={closeModal}
            />
          ),
        },
      });
    }
    return showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <NonF2fDiagnosisAppInstallQrCodeBottomSheet
            appInstallLink={APP_INSTALL_LINK_FROM_HOSPITAL_SEARCH}
            onClickOverlay={closeModal}
          />
        ),
      },
    });
  };

  const onCloseSearchAddressFilterModal = () => {
    setIsAddressFilterModalOpen(false);
  };

  const onUpdateAddress = (item: SearchKoreaAddressV2Vo) => {
    router.replace({
      query: {
        ...queries,
        regionCode: item.regionCode,
      },
    });
  };

  const onSelectAddress = (item: SearchKoreaAddressV2Vo) => {
    onUpdateAddress(item);
    onCloseSearchAddressFilterModal();
  };

  const openSearchAddressFilterModal = () => {
    setIsAddressFilterModalOpen(true);
  };

  const toggleNonF2fHospitalFilter = () => {
    router.replace({
      query: {
        ...queries,
        filter:
          filter === NeighborhoodHospitalFilter.F2fHospital
            ? NeighborhoodHospitalFilter.NonF2fHospital
            : NeighborhoodHospitalFilter.F2fHospital,
      },
    });
  };

  const selectNeighborhoodHospitalType = (type: NeighborhoodHospitalType) => {
    router.replace({
      query: {
        ...queries,
        type: type,
      },
    });

    closeModal();
  };
  const selectNeighborhoodKeyword = (department: NonFaceToFaceDepartment) => {
    router.replace({
      query: {
        ...queries,
        keyword: department.name,
      },
    });

    closeModal();
  };

  const openNeighborhoodHospitalTypeBottomSheet = () => {
    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <SelectBottomSheet
            title={'병원 종류를 선택해주세요'}
            list={neighborhoodHospitalTypes.list}
            onClick={selectNeighborhoodHospitalType}
            isSelected={(item) => item === type}
            getItemName={(item) => `${neighborhoodHospitalTypes.selectedTypeTitle(item)} 병원`}
            onClose={closeModal}
          />
        ),
      },
    });
  };
  const openNeighborhoodKeywordBottomSheet = () => {
    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <SelectBottomSheet
            title={'진료 과목을 선택해주세요'}
            list={departments}
            onClick={selectNeighborhoodKeyword}
            isSelected={(item) => item.name === keyword}
            getItemName={(item) => item.name}
            onClose={closeModal}
          />
        ),
      },
    });
  };

  const onClickRelatedMagazine = (magazinePid: string) => {
    moveToPage({
      webLink: `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`,
      mobileScreenName: ROUTES.MAGAZINE.DETAIL.appScreenName,
      params: {
        id: magazinePid,
      },
    });
  };
  const onClickRelatedFaq = (magazinePid: string) => {
    moveToPage({
      webLink: `${ROUTES.FAQ.DETAIL.path}/${magazinePid}`,
      mobileScreenName: ROUTES.FAQ.DETAIL.appScreenName,
      params: {
        id: magazinePid,
      },
    });
  };

  const getSearchAddressFilterModalProps = (): {
    siDoList: SearchKoreaAddressV2Vo[];
    siGunGuList: SearchKoreaAddressV2Vo[];
    dongList: SearchKoreaAddressV2Vo[];
    selectedRegion: SelectedRegion;
    onSelectAddress: (item: SearchKoreaAddressV2Vo) => void;
    onUpdateAddress: (item: SearchKoreaAddressV2Vo) => void;
    isOpened: boolean;
    isSejong: boolean;
    client: Client;
    switchRegionFilter: () => void;
  } => {
    return {
      switchRegionFilter: onCloseSearchAddressFilterModal,
      siDoList,
      siGunGuList,
      dongList,
      selectedRegion: koreaAddress.getSelectedRegionByDepth(queryRegion),
      onSelectAddress: onSelectAddress,
      onUpdateAddress: onUpdateAddress,
      isOpened: true,
      isSejong: queryRegion.isSejongRegion,
      client,
    };
  };

  const getNeighborhoodSeoProps = () => {
    return {
      title: `${koreaAddress.getAddressTitle(queryRegion)} ${neighborhoodHospitalTypes.selectedTypeTitle(
        type,
      )} 병원 비교 | ${keyword} (2025)`,
      description: `${neighborhoodHospitalTypes.selectedTypeTitle(
        type,
      )} 병원 정보와 후기를 바탕으로 ${koreaAddress.getAddressTitle(
        queryRegion,
      )} ${keyword} 병원 중 잘하는 곳을 위주로 추천해드릴게요.`,
      canonical: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.NEIGHBORHOOD.path}${addQueryString({
        keyword,
        regionCode,
        type,
      })}`,
    };
  };

  return {
    isLoadingHospitals,
    hospitals,
    nonF2fDoctors,
    queries,
    queryRegion,
    koreaAddress,
    faqs,
    magazines,
    matchedCategory,
    getSearchAddressFilterModalProps,
    isAddressFilterModalOpen,
    neighborhoodHospitalTypes,
    openNeighborhoodHospitalTypeBottomSheet,
    openSearchAddressFilterModal,
    toggleNonF2fHospitalFilter,
    openAppInstallBottomSheet,
    onClickRelatedFaq,
    onClickRelatedMagazine,
    getNeighborhoodSeoProps,
    departments,
    openNeighborhoodKeywordBottomSheet,
  };
};

export default useNeighborhoodHospitalViewModel;
